import {
  contactsQueryOptions,
  conversationMessagesInfiniteQueryOptions,
  ConversationType,
  createConversationMutationOptions,
  sendMessageMutationOptions,
  userHumanToHumanConversationsQueryOptions,
  type Contact,
  type Conversation,
  type Message,
} from '@/api/queries/conversations';
import type { ChatSidebarAction } from '@/components/chat';
import { ChatContainer, ChatInput, ChatMessage, type ChatInputRef } from '@/components/chat';
import { CreateNewConversation, ManageContacts } from '@/features/conversations/contacts';
import { getContactInitials } from '@/features/conversations/contacts/contactUtils';
import {
  ConversationHistoryItem,
  type ConversationHistoryItemType,
} from '@/features/conversations/ConversationHistoryItem';
import { WelcomeMessage } from '@/features/conversations/WelcomeMessage';
import { useAuth } from '@/hooks/useAuth';
import { useConversationId } from '@/hooks/useConversationId';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export default function ConversationPage() {
  const { t } = useTranslation('conversations');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreateNewConversationOpen, setIsCreateNewConversationOpen] = useState(false);
  const [isManageContactOpen, setIsManageContactOpen] = useState(false);
  const { generateConversationId, currentConversationId, setConversationId } = useConversationId({
    generateIfNotDefined: false,
  });

  const chatInputRef = useRef<ChatInputRef>(null);

  const { data: conversationsData } = useQuery({
    ...userHumanToHumanConversationsQueryOptions(),
    enabled: !!user,
  });

  const {
    data: conversationMessagesData,
    isLoading: isLoadingMessages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...conversationMessagesInfiniteQueryOptions(
      currentConversationId || '',
      !!user && !!currentConversationId
    ),
  });

  const allMessages = useMemo(() => {
    if (!conversationMessagesData?.pages) return [];
    return [...conversationMessagesData.pages].reverse().flatMap(page => page.messages);
  }, [conversationMessagesData]);

  const { data: contactsData } = useQuery({
    ...contactsQueryOptions(),
    enabled: !!user,
  });

  const sendMessageMutation = useMutation({
    ...sendMessageMutationOptions(),
    onSuccess: (_response, variables) => {
      if (!currentConversationId) return;

      const newMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: variables.conversationId,
        sender: {
          user_id: user?.user_id || null,
          contact_id: null,
        },
        content: variables.content,
        created_at: new Date().toISOString(),
        read_at: null,
      };

      queryClient.setQueryData(
        [
          'conversations',
          ConversationType.HUMAN_TO_HUMAN,
          currentConversationId,
          'messages',
          'infinite',
        ],
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any, index: number) => {
              if (index === 0) {
                return {
                  ...page,
                  messages: [...page.messages, newMessage],
                };
              }
              return page;
            }),
          };
        }
      );
    },
  });
  const createConversationMutation = useMutation(createConversationMutationOptions());

  const contactIdToName = useMemo(() => {
    if (!contactsData) return {};
    return contactsData.reduce((acc: Record<string, string>, contact: Contact) => {
      acc[contact.id] = contact.name;
      return acc;
    }, {});
  }, [contactsData]);

  if (!user) return null;

  const conversationHistory: ConversationHistoryItemType[] =
    conversationsData?.map((conv: Conversation) => ({
      id: conv.id,
      title: conv.title,
      isActive: conv.id === currentConversationId,
      participantsNames: conv.participant_names,
      ownerName: conv.owner_name,
    })) || [];

  const handleSelectConversation = (conversationId: string) => {
    setConversationId(conversationId);
    chatInputRef.current?.clear();
  };

  const handleNewConversation = () => {
    setIsCreateNewConversationOpen(true);
    chatInputRef.current?.clear();
  };

  const handleCreateConversation = async (title: string, contact: Contact) => {
    const conversationCandidateId = generateConversationId();

    try {
      const createConversationResponse = await createConversationMutation.mutateAsync({
        conversationId: conversationCandidateId,
        conversationType: ConversationType.HUMAN_TO_HUMAN,
        conversationTitle: title,
        recipientContactIds: [contact.id],
      });

      const existingConversationId =
        createConversationResponse.existing_conversation_id_with_same_participants;
      let newConversationId;
      if (!createConversationResponse.created && existingConversationId) {
        newConversationId = existingConversationId;
        toast.success(t('newConversation.conversationAlreadyExists'));
      } else {
        newConversationId = conversationCandidateId;
        queryClient.invalidateQueries({
          queryKey: ['conversations', ConversationType.HUMAN_TO_HUMAN],
        });
      }

      setIsCreateNewConversationOpen(false);
      setConversationId(newConversationId);
      chatInputRef.current?.clear();

      return { success: true };
    } catch (error) {
      toast.error(t('errors.createConversationFailed'));
      return { success: false };
    }
  };

  const handleContactsAction = () => {
    setIsManageContactOpen(true);
  };

  const sidebarActions: ChatSidebarAction[] = [
    {
      key: 'contacts',
      label: 'Contacts',
      icon: Users,
      onClick: handleContactsAction,
    },
  ];

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || !currentConversationId) return;

    try {
      await sendMessageMutation.mutateAsync({
        conversationId: currentConversationId,
        content: message.trim(),
      });

      chatInputRef.current?.clear();
    } catch (error) {
      toast.error(t('errors.sendMessageFailed', { message: error }));
    }
  };

  const renderMessages = () => {
    if (!currentConversationId || isLoadingMessages) {
      return [];
    }

    return allMessages;
  };

  const renderMessage = (message: Message) => {
    const isCurrentUserMessage = message.sender?.user_id === user.user_id;

    const contactName = message.sender?.contact_id
      ? contactIdToName[message.sender.contact_id]
      : null;
    const initials = contactName ? getContactInitials(contactName) : '??';

    return (
      <ChatMessage
        key={message.id}
        content={message.content}
        isCurrentUserMessage={isCurrentUserMessage}
        avatar={
          !isCurrentUserMessage ? (
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-medium">
              {initials}
            </div>
          ) : undefined
        }
      />
    );
  };

  const renderHistoryItem = (conversation: ConversationHistoryItemType) => {
    return (
      <ConversationHistoryItem
        key={conversation.id}
        {...conversation}
        onClick={() => handleSelectConversation(conversation.id)}
      />
    );
  };

  return (
    <>
      <ChatContainer
        welcomeMessage={
          !allMessages.length && !isLoadingMessages ? (
            <WelcomeMessage
              selectedConversation={!!currentConversationId}
              contactNames={
                conversationHistory.find(c => c.id === currentConversationId)?.participantsNames ||
                []
              }
            />
          ) : undefined
        }
        currentConversationMessages={renderMessages()}
        conversations={conversationHistory}
        renderMessage={renderMessage}
        renderHistoryItem={renderHistoryItem}
        onNewConversation={handleNewConversation}
        sidebarActions={sidebarActions}
        chatInput={
          !!currentConversationId && (
            <ChatInput
              ref={chatInputRef}
              onSend={handleSendMessage}
              actionButtons={[]}
              placeholder={t('chatInput.placeholder')}
              disabled={isLoadingMessages}
            />
          )
        }
        isLoadingMessages={isLoadingMessages}
        onLoadMore={() => fetchNextPage()}
        hasMoreMessages={hasNextPage}
        isLoadingMore={isFetchingNextPage}
      />

      {/* Create New Conversation */}
      <CreateNewConversation
        isOpen={isCreateNewConversationOpen}
        onClose={() => setIsCreateNewConversationOpen(false)}
        onCreateConversation={handleCreateConversation}
        isLoading={createConversationMutation.isPending}
      />

      {/* Manage Contacts */}
      <ManageContacts isOpen={isManageContactOpen} onClose={() => setIsManageContactOpen(false)} />
    </>
  );
}
